import { Fade } from 'react-awesome-reveal';
import { 
  FaCloudUploadAlt, 
  FaPaintBrush, 
  FaServer, 
  FaShieldAlt,
  FaUser,
  FaTrophy,
  FaBuilding,
  FaCalendarAlt,
  FaCode,
  FaRobot
} from '../../common/Icons';
import './About.css'; 

const About = () => {
  return (
    <div className="about-container full-page-section section-padding" id="about">
      <Fade direction="up" triggerOnce={true}>
        <h2 className='bold'> // About me</h2>
      </Fade>

      <div className="about-grid">
        <Fade direction="up" triggerOnce={true} delay={100}>
          <div className="about-intro-text">
            <div className="cybersecurity-title">
              <FaUser className="icon" />
              <h3>Intro</h3>
            </div>
            <p className="about-intro">
              With over <b>6 years of experience as a Software Engineer</b>, my career is defined by building <b>robust, secure, and scalable architectures</b>. 
              Currently at Linde (KION Group), I develop the WMS solutions that power industrial automation, bridging the gap between code and physical execution. 
              Previously at MediaMarkt Iberia, I led modernization initiatives, transforming legacy systems into a modern cloud stack (.NET 8, React, GCP).
            </p>
            
            <div className="cybersecurity-title">
              <FaShieldAlt className="icon" />
              <h3>Cybersecurity Vision (DevSecOps)</h3>
            </div>
            <p>
              <b>My Master's in Cybersecurity & Privacy isn't just a degree; it's a mindset I apply daily.</b> I have performed internal white-box audits that identified and remediated vulnerabilities, bridging the gap between a senior developer's perspective and a security-first approach.
            </p>
          </div>
        </Fade>

        <Fade direction="up" triggerOnce={true} delay={100}>
          <div className="about-achievements">
            <div className='achieve-title'>
              <FaTrophy  className="icon" />
              <h3>Key Achievements</h3>
            </div>
            
            <div className="achievements-grid">
              <div className="achievement-card">
                <FaCloudUploadAlt className="icon" />
                <div>
                  <h4>Cloud-Native Project Lead</h4>
                  <p>I led the development of the back-office for Locker management, <b>the first 100% cloud-native project at MM Spain.</b></p>
                </div>
              </div>
              <div className="achievement-card">
                <FaPaintBrush className="icon" />
                <div>
                  <h4>UI/UX Impact</h4>
                  <p>Built the new time-tracking application (.NET & React) from scratch <b>for +400 employees, creating a clean and positive user experience.</b></p>
                </div>
              </div>
              <div className="achievement-card">
                <FaServer className="icon" />
                <div>
                  <h4>Cloud Infrastructure</h4>
                  <p>Contributed to configuring our GCP infrastructure using Kubernetes and FluxCD (GitOps), and implemented .NET APIs for Cloud Storage.</p>
                </div>
              </div>
            </div>
          </div>
        </Fade>
      </div>

      <Fade direction="up" triggerOnce={true} delay={100}>
        <h2 className="experience-title">// Professional Experience</h2>
      </Fade>

      <Fade direction="up" triggerOnce={true} delay={50}>
        <div className="job-card">
          <div className='job-title'>
            <FaRobot /> 
            <h3>Senior Software Developer (Business Automation Unit)</h3>
          </div>
          <div className="job-meta">
            <p className="job-company">
              <FaBuilding /> Linde Material Handling Iberica (KION Group)
            </p>
            <p className="job-date">
              <FaCalendarAlt /> Jan 2026 - Present
            </p>
          </div>
          <ul className="job-duties">
            <li>Developing and customizing high-performance Warehouse Management Systems (WMS) within the Automation Business Unit.</li>
            <li>Orchestrating the integration between backend software and physical automation hardware (AGVs, AMRs).</li>
            <li>Designing scalable and secure architectures to ensure 24/7 reliability in critical logistics infrastructure.</li>
            <li>Tailoring software logic to optimize complex material flows for global clients.</li>
          </ul>
          
          <div className="job-tech-stack">
            <span className="tech-tag">.NET / C#</span>
            <span className="tech-tag">WMS</span>
            <span className="tech-tag">Industrial Automation</span>
            <span className="tech-tag">AGV / AMR</span>
            <span className="tech-tag">Distributed Systems</span>
            <span className="tech-tag">System Integration</span>
          </div>
        </div>
      </Fade>

      <Fade direction="up" triggerOnce={true} delay={100}>
        <div className="job-card">
          <div className='job-title'>
            <FaCode />
            <h3>Senior Software Developer</h3>
          </div>
          <div className="job-meta">
            <p className="job-company">
              <FaBuilding /> MediaMarkt Iberia
            </p>
            <p className="job-date">
              <FaCalendarAlt /> May 2022 - Jan 2026
            </p>
          </div>
          <ul className="job-duties">
            <li>Lead on the 100% cloud-native Locker management back-office (Next.js, React, GCP).</li>
            <li>Developed the new time-tracking app (.NET 8, React) for +400 users (Final Degree Project).</li>
            <li>Executed key migrations from legacy systems (ColdFusion, .NET Framework) to .NET 8 and React.</li>
            <li>Performed internal white-box security audits, identifying and remediating vulnerabilities.</li>
            <li>Contributed to GCP/Kubernetes/GitOps platform configuration.</li>
          </ul>
          
          <div className="job-tech-stack">
            <span className="tech-tag">Next.js</span>
            <span className="tech-tag">React</span>
            <span className="tech-tag">.NET 8</span>
            <span className="tech-tag">GCP</span>
            <span className="tech-tag">Kubernetes</span>
            <span className="tech-tag">GitOps</span>
            <span className="tech-tag">Security</span>
          </div>
        </div>
      </Fade>

      <Fade direction="up" triggerOnce={true} delay={100}>
        <div className="job-card">
          <div className='job-title'>
            <FaCode />
            <h3>.NET Software Developer</h3>
          </div>
          <div className="job-meta">
            <p className="job-company">
              <FaBuilding /> Matrix Development System
            </p>
            <p className="job-date">
              <FaCalendarAlt /> June 2019 - May 2022
            </p>
          </div>
          <ul className="job-duties">
            <li>Developed an OCR solution to automate accounting by scanning invoices and fiscal documents.</li>
            <li>Built server-side services (Windows Services, Workers) with multi-threading.</li>
            <li>Core development in C#, .NET, .NET Core, WPF (MVVM), and Microsoft SQL Server.</li>
          </ul>
          <div className="job-tech-stack">
            <span className="tech-tag">.NET Core</span>
            <span className="tech-tag">C#</span>
            <span className="tech-tag">WPF</span>
            <span className="tech-tag">SQL Server</span>
            <span className="tech-tag">OCR</span>
            <span className="tech-tag">Windows Services</span>
          </div>
        </div>
      </Fade>
      
    </div>
  );
};

export default About;